/**
 * Module Life Performance — primitives UI partagées.
 *
 * Couleurs et helpers réutilisés par les écrans du module. Même grammaire
 * visuelle que le module Santé (fond sombre, cartes translucides) avec un
 * accent propre à Performance pour le différencier.
 */
import React from 'react'
import { Box, Chip, CircularProgress, LinearProgress, Typography } from '@mui/material'

export const PERF_ACCENT = '#ff6a3d' // orange « performance »
export const PERF_ACCENT_2 = '#5b8cff' // bleu secondaire (graphes)
export const PANEL_BG = 'rgba(255,255,255,0.04)'
export const PAGE_BG = '#0b0f1f'

export const cardSx = {
  bgcolor: PANEL_BG,
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 3,
  p: 2.5,
}

export const darkFieldSx = {
  '& .MuiInputBase-input': { color: '#fff' },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255,255,255,0.4)',
    opacity: 1,
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiInputLabel-root.Mui-focused': { color: PERF_ACCENT },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255,255,255,0.45)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: PERF_ACCENT,
  },
  '& .MuiSvgIcon-root': { color: 'rgba(255,255,255,0.6)' },
  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.45)' },
}

// Libellés humains.
export const SESSION_TYPE_LABELS = {
  strength: 'Renforcement',
  endurance: 'Endurance',
  interval: 'Fractionné',
  long: 'Sortie longue',
  technique: 'Technique',
  mobility: 'Mobilité',
  recovery: 'Récupération',
  test: 'Test',
  rest: 'Repos',
}

export const GOAL_TYPE_LABELS = {
  distance: 'Distance',
  weight: 'Poids',
  muscle_mass: 'Masse musculaire',
  strength: 'Force',
  frequency: 'Fréquence',
  health: 'Santé',
  technique: 'Technique',
}

export const RECOVERY_LABELS = {
  sleep_hours: 'Sommeil',
  sleep_quality: 'Qualité de sommeil',
  resting_hr: 'FC au repos',
  hrv: 'HRV',
  body_battery: 'Body Battery',
  stress: 'Stress',
  readiness: 'Forme',
  soreness: 'Courbatures',
}

export const SEVERITY_COLOR = {
  info: '#5b8cff',
  suggestion: PERF_ACCENT,
  warning: '#ff4d4f',
}

export const WEEKDAYS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

export function Loading({ label = 'Chargement…' }) {
  return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <CircularProgress sx={{ color: PERF_ACCENT }} />
      <Typography sx={{ color: 'rgba(255,255,255,0.6)', mt: 2 }}>{label}</Typography>
    </Box>
  )
}

export function StatCard({ label, value, suffix = '', accent = PERF_ACCENT }) {
  return (
    <Box sx={{ ...cardSx, minWidth: 130 }}>
      <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
        {label}
      </Typography>
      <Typography sx={{ color: accent, fontWeight: 800, fontSize: 26, mt: 0.5 }}>
        {value}
        <span style={{ fontSize: 14, opacity: 0.7 }}>{suffix}</span>
      </Typography>
    </Box>
  )
}

export function ProgressBar({ pct, accent = PERF_ACCENT }) {
  const v = Math.max(0, Math.min(100, pct || 0))
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress
        variant="determinate"
        value={v}
        sx={{
          flex: 1,
          height: 8,
          borderRadius: 4,
          bgcolor: 'rgba(255,255,255,0.1)',
          '& .MuiLinearProgress-bar': { backgroundColor: accent },
        }}
      />
      <Typography sx={{ color: '#fff', fontSize: 12, width: 38, textAlign: 'right' }}>
        {Math.round(v)}%
      </Typography>
    </Box>
  )
}

export function Pill({ label, color = PERF_ACCENT }) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: `${color}22`,
        color,
        border: `1px solid ${color}55`,
        fontWeight: 600,
      }}
    />
  )
}

export const SectionTitle = ({ children }) => (
  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 18, mb: 1.5 }}>
    {children}
  </Typography>
)
